import * as React from "react";
import clsx from "clsx";
import {
  BarChart3,
  BellRing,
  BookOpen,
  ChevronDown,
  ChevronUp,
  CloudLightning,
  Database,
  Globe,
  History,
  LifeBuoy,
  ListChecks,
  PhoneCall,
  PiggyBank,
  Search,
  ThumbsDown,
  ThumbsUp,
  TrendingDown,
  X,
} from "lucide-react";
import { Button } from "../../../../ui/components/Button";
import { AuthTransitionScreen } from "../../../../ui/components/AuthTransitionScreen";
import { useT } from "../../../lib/i18n";
import { ProApiClientError } from "../../../lib/api/pro-api.client";
import {
  insightErrorCode,
  type InsightDetails,
  type InsightEvidenceWindow,
  type InsightFeedback,
  type InsightGenerationMeta,
  type InsightKind,
  type InsightSource,
  type InsightSupportFields,
  type InsightSupportRequest,
  type InsightUrgency,
  type ProductInsight,
} from "../../../lib/api/insights";
import { SupportRequestModal } from "./SupportRequestModal";
import * as s from "./InsightCard.css";

type T = ReturnType<typeof useT>;

export const LION_SUPPORT_PHONE = "1-435-244-3352";

const KIND_ICONS: Record<InsightKind, React.ReactNode> = {
  alert_explanation: <BellRing size={13} aria-hidden />,
  performance: <TrendingDown size={13} aria-hidden />,
  optimization: <PiggyBank size={13} aria-hidden />,
  resilience: <CloudLightning size={13} aria-hidden />,
};

const SOURCE_ICONS: Record<InsightSource["kind"], React.ReactNode> = {
  knowledge_base: <BookOpen size={11} aria-hidden />,
  telemetry: <Database size={11} aria-hidden />,
  alert_history: <History size={11} aria-hidden />,
  external: <Globe size={11} aria-hidden />,
};

export const URGENCY_RANK: Record<InsightUrgency, number> = {
  critical: 0,
  high: 1,
  medium: 2,
  low: 3,
  info: 4,
};

export function formatRelative(iso: string): string {
  const diffMs = new Date(iso).getTime() - Date.now();
  const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  const abs = Math.abs(diffMs);
  if (abs < 60 * 60_000) return rtf.format(Math.round(diffMs / 60_000), "minute");
  if (abs < 24 * 60 * 60_000) return rtf.format(Math.round(diffMs / (60 * 60_000)), "hour");
  return rtf.format(Math.round(diffMs / (24 * 60 * 60_000)), "day");
}

function formatTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

export function formatUsd(n: number): string {
  return n < 0.01 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
}

/** User-facing message for a failed details request, keyed by the backend's error code. */
function customerDetailsErrorMessage(err: unknown, t: T): string {
  switch (insightErrorCode(err)) {
    case "no_guidance":
      return t("pro.products.insights.error.noGuidance");
    case "budget_exceeded":
    case "ai_not_configured":
      return t("pro.products.insights.error.paused", { phone: LION_SUPPORT_PHONE });
    case "busy":
      return t("pro.products.insights.error.busy");
    default:
      return t("pro.products.insights.error.generic");
  }
}

/**
 * User-facing message for a failed details request. Super-admins also get the
 * error code and any provider detail, so config problems are visible in the UI.
 */
export function detailsErrorMessage(err: unknown, t: T, showDiagnostics = false): string {
  const message = customerDetailsErrorMessage(err, t);
  if (!showDiagnostics) return message;
  const code = insightErrorCode(err);
  const detail = err instanceof ProApiClientError && typeof err.data?.detail === "string" ? err.data.detail : null;
  const diagnostics = [code, detail].filter(Boolean).join(": ");
  return diagnostics ? `${message} [${diagnostics}]` : message;
}

export type InsightActions = {
  /** Generates (or fetches stored) details. Called the first time an Insight is opened. */
  onLoadDetails?: (insight: ProductInsight) => Promise<ProductInsight>;
  onFeedback?: (insight: ProductInsight, feedback: InsightFeedback) => void;
  onDismiss?: (insight: ProductInsight) => void;
  onRequestSupport?: (insight: ProductInsight, fields: InsightSupportFields) => Promise<InsightSupportRequest>;
  /** Jump to Historical Data for an evidence window. */
  onViewEvidence?: (window: InsightEvidenceWindow) => void;
};

/** Evidence, self-service steps, and escalation guidance. Shared by the card and the alert modal. */
export function InsightDetailsView({
  details,
  onViewEvidence,
}: {
  details: InsightDetails;
  onViewEvidence?: InsightActions["onViewEvidence"];
}) {
  const t = useT();
  return (
    <div className={s.detailGrid}>
      <section className={s.detailBlock} aria-label={t("pro.products.insights.why")}>
        <h4 className={s.detailHeading}>
          <Search size={12} aria-hidden /> {t("pro.products.insights.why")}
        </h4>
        <ul className={s.evidenceList}>
          {details.evidence.map((e, i) => (
            <li key={i} className={s.evidenceItem}>
              <span>{e.label}</span>
              {e.detail ? <span className={s.evidenceDetail}>{e.detail}</span> : null}
              {e.window && onViewEvidence ? (
                <button type="button" className={s.linkButton} onClick={() => onViewEvidence(e.window!)}>
                  <BarChart3 size={12} aria-hidden /> {t("pro.products.insights.viewData")}
                </button>
              ) : null}
            </li>
          ))}
        </ul>
      </section>

      <section className={s.detailBlock} aria-label={t("pro.products.insights.whatYouCanDo")}>
        <h4 className={s.detailHeading}>
          <ListChecks size={12} aria-hidden /> {t("pro.products.insights.whatYouCanDo")}
        </h4>
        {details.selfServiceSteps.length > 0 ? (
          <ol className={s.list}>
            {details.selfServiceSteps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        ) : (
          <p className={s.emptyStep}>{t("pro.products.insights.noAction")}</p>
        )}
      </section>

      {details.contactLionIf.length > 0 ? (
        <section className={s.detailBlock} aria-label={t("pro.products.insights.contactLionIf")}>
          <h4 className={s.detailHeading}>
            <PhoneCall size={12} aria-hidden /> {t("pro.products.insights.contactLionIf")}
          </h4>
          <ul className={s.list}>
            {details.contactLionIf.map((c, i) => (
              <li key={i}>{c}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}

export function InsightSources({ details }: { details: InsightDetails }) {
  const t = useT();
  return (
    <div className={s.sourcesRow}>
      <span>{t("pro.products.insights.basedOn")}</span>
      {details.sources.map((src, i) => (
        <span key={i} className={s.sourceChip}>
          {SOURCE_ICONS[src.kind]}
          {src.label}
        </span>
      ))}
      <span>· {t(`pro.products.insights.confidence.${details.confidence}`)}</span>
    </div>
  );
}

function formatGeneration(g: InsightGenerationMeta): string {
  return `${g.model} · ${formatTokens(g.inputTokens)} in / ${formatTokens(g.outputTokens)} out${
    g.cacheReadTokens ? ` / ${formatTokens(g.cacheReadTokens)} cached` : ""
  } · ${formatUsd(g.costUsd)}`;
}

/** Per-tier model, token and cost lines. Only rendered for super-admins. */
export function InsightUsageLine({ insight }: { insight: ProductInsight }) {
  const t = useT();
  const details = insight.details;
  return (
    <div className={s.usageLine}>
      <div>
        {t("pro.products.insights.usage.headline")}{" "}
        {insight.headlineGeneration
          ? formatGeneration(insight.headlineGeneration)
          : t("pro.products.insights.usage.template")}
      </div>
      <div>
        {t("pro.products.insights.usage.details")}{" "}
        {details?.generation
          ? formatGeneration(details.generation)
          : details
            ? t("pro.products.insights.usage.unrecorded")
            : t("pro.products.insights.usage.notGenerated")}
      </div>
    </div>
  );
}

/** Confirmation shown after a support request is recorded. */
export function SupportConfirmation({ request }: { request: InsightSupportRequest }) {
  const t = useT();
  return (
    <span className={s.supportConfirm} role="status">
      {request.deliveryStatus === "delivered"
        ? t("pro.products.insights.support.opened", { reference: request.reference })
        : t("pro.products.insights.support.recorded", { reference: request.reference, phone: LION_SUPPORT_PHONE })}
    </span>
  );
}

/** Feedback, support request and (optionally) dismiss. */
export function InsightFooter({
  insight,
  onFeedback,
  onRequestSupport,
  showUsage,
}: Pick<InsightActions, "onFeedback" | "onRequestSupport"> & {
  insight: ProductInsight;
  showUsage?: boolean;
}) {
  const t = useT();
  const [request, setRequest] = React.useState<InsightSupportRequest | null>(null);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [requesting, setRequesting] = React.useState(false);
  const [supportError, setSupportError] = React.useState<string | null>(null);

  const handleSupport = async (fields: InsightSupportFields) => {
    if (!onRequestSupport || requesting) return;
    setRequesting(true);
    setSupportError(null);
    try {
      setRequest(await onRequestSupport(insight, fields));
      setModalOpen(false);
    } catch {
      setSupportError(t("pro.products.insights.support.failed", { phone: LION_SUPPORT_PHONE }));
    } finally {
      setRequesting(false);
    }
  };

  return (
    <div className={s.footerRow}>
      {onFeedback ? (
        <span className={s.feedbackGroup}>
          {t("pro.products.insights.feedback.prompt")}
          <button
            type="button"
            className={s.feedbackButton}
            aria-pressed={insight.feedback === "helpful"}
            aria-label={t("pro.products.insights.feedback.helpful")}
            onClick={() => onFeedback(insight, "helpful")}
          >
            <ThumbsUp size={14} />
          </button>
          <button
            type="button"
            className={s.feedbackButton}
            aria-pressed={insight.feedback === "not_helpful"}
            aria-label={t("pro.products.insights.feedback.notHelpful")}
            onClick={() => onFeedback(insight, "not_helpful")}
          >
            <ThumbsDown size={14} />
          </button>
        </span>
      ) : null}
      <span className={s.headerSpacer} />
      {showUsage ? <InsightUsageLine insight={insight} /> : null}
      {onRequestSupport ? (
        request ? (
          <SupportConfirmation request={request} />
        ) : (
          <>
            <Button
              type="button"
              size="sm"
              color="neutral"
              variant="outline"
              iconLeft={<LifeBuoy size={14} />}
              onClick={() => {
                setSupportError(null);
                setModalOpen(true);
              }}
            >
              {t("pro.products.insights.support.button")}
            </Button>
            <SupportRequestModal
              open={modalOpen}
              subject={insight.title}
              alertCode={insight.alertCode}
              steps={insight.details?.selfServiceSteps}
              loading={requesting}
              error={supportError}
              onCancel={() => setModalOpen(false)}
              onSubmit={(fields) => void handleSupport(fields)}
            />
          </>
        )
      ) : null}
    </div>
  );
}

export function InsightCard({
  insight,
  defaultExpanded = false,
  showUsage = false,
  onLoadDetails,
  onFeedback,
  onDismiss,
  onRequestSupport,
  onViewEvidence,
}: InsightActions & {
  insight: ProductInsight;
  defaultExpanded?: boolean;
  showUsage?: boolean;
}) {
  const t = useT();
  const [expanded, setExpanded] = React.useState(defaultExpanded && Boolean(insight.details));
  const [loadingDetails, setLoadingDetails] = React.useState(false);
  const [detailsError, setDetailsError] = React.useState<string | null>(null);
  const resolved = insight.status === "resolved";
  const details = insight.details;

  const handleToggle = async () => {
    if (expanded) {
      setExpanded(false);
      return;
    }
    setExpanded(true);
    if (details || !onLoadDetails || loadingDetails) return;
    setLoadingDetails(true);
    setDetailsError(null);
    try {
      await onLoadDetails(insight);
    } catch (err) {
      setDetailsError(detailsErrorMessage(err, t, showUsage));
    } finally {
      setLoadingDetails(false);
    }
  };

  return (
    <article className={clsx(s.card, resolved && s.cardResolved)} data-urgency={insight.urgency} aria-label={insight.title}>
      <div className={s.headerRow}>
        <span className={s.urgencyChip}>
          <span className={s.urgencyDot} aria-hidden />
          {resolved ? t("pro.products.insights.resolved") : t(`pro.products.insights.urgency.${insight.urgency}`)}
        </span>
        <span className={s.kindLabel}>
          {KIND_ICONS[insight.kind]}
          {t(`pro.products.insights.kind.${insight.kind}`)}
          {insight.alertCode ? ` · ${insight.alertCode}` : ""}
        </span>
        {insight.occurrenceCount > 1 ? (
          <span
            className={s.occurrenceChip}
            title={t("pro.products.insights.occurrences", { count: insight.occurrenceCount })}
          >
            ×{insight.occurrenceCount}
          </span>
        ) : null}
        <span className={s.metaText} title={new Date(insight.createdAt).toLocaleString()}>
          {formatRelative(insight.createdAt)}
        </span>
        <span className={s.headerSpacer} />
        {onDismiss && !resolved ? (
          <button
            type="button"
            className={s.iconButton}
            aria-label={t("pro.products.insights.dismissAria")}
            title={t("pro.products.insights.dismiss")}
            onClick={() => onDismiss(insight)}
          >
            <X size={16} />
          </button>
        ) : null}
      </div>

      <h3 className={s.title}>{insight.title}</h3>
      <p className={s.summary}>{insight.summary}</p>

      {expanded && details ? (
        <>
          <InsightDetailsView details={details} onViewEvidence={onViewEvidence} />
          <InsightSources details={details} />
        </>
      ) : null}
      {expanded && loadingDetails ? (
        <div className={s.analyzing}>
          <AuthTransitionScreen variant="compact" message={t("pro.products.insights.analyzing")} />
        </div>
      ) : null}
      {expanded && detailsError ? <p className={s.errorText}>{detailsError}</p> : null}

      <button type="button" className={s.toggleDetails} aria-expanded={expanded} onClick={() => void handleToggle()}>
        {expanded ? <ChevronUp size={12} aria-hidden /> : <ChevronDown size={12} aria-hidden />}
        {expanded ? t("pro.products.insights.hideDetails") : t("pro.products.insights.showDetails")}
      </button>

      {expanded && (details || detailsError) ? (
        <InsightFooter
          insight={insight}
          onFeedback={details ? onFeedback : undefined}
          onRequestSupport={resolved ? undefined : onRequestSupport}
          showUsage={showUsage}
        />
      ) : null}
    </article>
  );
}
